<script setup>
import apiDriveTicket from "@/services/http/api-drive/client/ticket";
import apiDriveMagasin from "@/services/http/api-drive/magasin";
import { loadPageWithLoader } from "@/utils/webpack-helper";
import { useTrackingPage } from "@/mixins/tracking/all-pages";
import dateUtil from "@/utils/date.js";
import { formatPrix } from "@/composables/text-transformation";
import { useContextStore } from "@/store/context";
import { useClientStore } from "@/store/client";
import { useMagasinStore } from "@/store/magasin";

definePageMeta({
  key: (route) => route.fullPath,
  middleware: "authenticated"
});

const emit = defineEmits(["updateShowPreferences"]);

const app = useNuxtApp();
const { trackPage } = useTrackingPage();
const { query } = useRoute();
const { layout } = storeToRefs(useContextStore());
const { client, isPorteurCarteCora, isPorteurCarteMalin, hasCarteCarrefour } =
  storeToRefs(useClientStore());
const { isFidCarrefour } = storeToRefs(useMagasinStore());

const achatIsLoading = ref(false);
const limit = ref(7);
const haveMoreAchats = ref(true);
const isMagasinTeasing = ref(false);
const achatsMagasin = ref([]);
const errorTickets = ref(null);
const listeMagasins = ref([]);

onBeforeMount(() => {
  trackPage(trackingCodes.PAGETYPE.monCompte);
  emit("updateShowPreferences", filtersDisplay.value);
});

onUpdated(() => {
  emit("updateShowPreferences", filtersDisplay.value);
});

onMounted(() => {
  getAchats();
});

const { data: mags } = await useAsyncData((app) =>
  app.$apiDrive.use(apiDriveMagasin).magasins()
);
if (mags.value) {
  listeMagasins.value = mags.value;
}

const component = computed(() => {
  return loadPageWithLoader(
    lazyComponents[
      `/components/pages/mon-compte/mes-commandes/en-magasin/${layout.value}.vue`
    ]
  );
});

const appliedFilters = computed(() => {
  let filtresList = [];
  if (query.montant_total_ttc_min || query.montant_total_ttc_max) {
    filtresList.push({
      code: "montant",
      libelle: libelleMontant.value,
      valeur: {
        montant_total_ttc_min: query.montant_total_ttc_min,
        montant_total_ttc_max: query.montant_total_ttc_max
      }
    });
  }
  if (query.date_min || query.date_max) {
    filtresList.push({
      code: "date",
      libelle: libellesPeriode.value.libelle,
      valeur: {
        date_min: query.date_min,
        date_max: query.date_max
      }
    });
  }
  if (query.mag_id) {
    filtresList.push({
      code: "magasin",
      libelle: libelleMagasin.value,
      valeur: query.mag_id
    });
  }
  return filtresList;
});

const libelleMagasin = computed(() => {
  if (query.mag_id && listeMagasins.value.length > 0) {
    const magasinSelected = listeMagasins.value.find(
      (magasin) => magasin.id === query.mag_id
    );
    if (magasinSelected && !magasinSelected.attributes.ticket_dematerialise) {
      isMagasinTeasing.value = true;
    }
    return magasinSelected
      ? `${magasinSelected.attributes.cp.substring(0, 2)} - ${
          magasinSelected.attributes.magasin
        }`
      : "Magasin";
  } else {
    return "Magasin";
  }
});

const libelleMontant = computed(() => {
  if (query.montant_total_ttc_min && query.montant_total_ttc_max) {
    return (
      "entre " +
      formatPrix(query.montant_total_ttc_min) +
      " et " +
      formatPrix(query.montant_total_ttc_max) +
      " €"
    );
  } else if (query.montant_total_ttc_min) {
    /// WORDING A VALIDER
    return "à partir de  " + formatPrix(query.montant_total_ttc_min) + " €";
  } else if (query.montant_total_ttc_max) {
    /// WORDING A VALIDER
    return "jusqu'à " + formatPrix(query.montant_total_ttc_max) + " €";
  }
});

const listeMagasinsSorted = computed(() => {
  const filteredListe = listeMagasins.value.filter((magasin) => {
    return magasin.attributes.ticket_dematerialise;
  });
  const sortedListe = filteredListe.sort((a, b) => {
    if (a.attributes.cp.substring(0, 2) > b.attributes.cp.substring(0, 2)) {
      return 1;
    }
    if (a.attributes.cp.substring(0, 2) < b.attributes.cp.substring(0, 2)) {
      return -1;
    }
    if (a.attributes.magasin > b.attributes.magasin) {
      return 1;
    }
    if (a.attributes.magasin < b.attributes.magasin) {
      return -1;
    }
  });
  if (client.value.id_magasin) {
    const magasinClient = sortedListe.findIndex(
      (magasin) => magasin.id === client.value.id_magasin.toString()
    );
    sortedListe.unshift(sortedListe.splice(magasinClient, 1)[0]);
  }
  return sortedListe;
});

const libellesPeriode = computed(() => {
  const today = new Date();
  if (
    query.date_min ===
      dateUtil.formatRedevanceDate(dateUtil.startOf(today, "month")) &&
    query.date_max ===
      dateUtil.formatRedevanceDate(dateUtil.endOf(today, "month"))
  ) {
    return { libelle: "Ce mois-ci", key: "thisMonth" };
  } else if (
    query.date_min ===
      dateUtil.formatRedevanceDate(
        dateUtil.startOf(dateUtil.subtract(today, "month", 1), "month")
      ) &&
    query.date_max ===
      dateUtil.formatRedevanceDate(
        dateUtil.endOf(dateUtil.subtract(today, "month", 1), "month")
      )
  ) {
    return { libelle: "Le mois dernier", key: "lastMonth" };
  } else if (
    query.date_min ===
      dateUtil.formatRedevanceDate(
        dateUtil.startOf(dateUtil.subtract(today, "month", 2), "month")
      ) &&
    query.date_max ===
      dateUtil.formatRedevanceDate(dateUtil.endOf(today, "month"))
  ) {
    return { libelle: "Les 3 derniers mois", key: "lastThreeMonth" };
  } else {
    return { libelle: "Période personnalisée", key: "custom" };
  }
});

const filtersDisplay = computed(() => {
  return (
    (!(
      achatsMagasin.value.length === 0 &&
      appliedFilters.value.length === 0 &&
      query.garantie === "0"
    ) &&
      (isPorteurCarteMalin.value || isPorteurCarteCora.value) &&
      !errorTickets.value) ||
    achatIsLoading.value
  );
});

const showSeeMoreResults = computed(() => {
  return (
    haveMoreAchats.value &&
    achatsMagasin.value.length % limit.value === 0 &&
    achatsMagasin.value.length !== 0 &&
    !achatIsLoading.value
  );
});

const appliquerFiltres = async (filtre, libelle) => {
  await navigateTo({ query: { ...query, ...filtre } });
};

const supprimerFiltre = async (code) => {
  const queryValue = Object.assign({}, query);
  switch (code) {
    case "montant":
      delete queryValue.montant_total_ttc_min;
      delete queryValue.montant_total_ttc_max;
      break;
    case "date":
      delete queryValue.date_min;
      delete queryValue.date_max;
      break;
    case "magasin":
      delete queryValue.mag_id;
      break;
  }
  await navigateTo({ query: queryValue }, { replace: true });
};

const supprimerToutFiltres = async () => {
  await navigateTo({
    query: { type_carte: query.type_carte }
  });
};

const getAchats = async () => {
  errorTickets.value = null;
  achatIsLoading.value = true;
  const params = { ...query };
  if (params.mag_id) {
    params.magasin_id = params.mag_id;
    delete params.mag_id;
  }
  try {
    const res = await app.$apiDrive.use(apiDriveTicket).tickets({
      limit: 7,
      offset: achatsMagasin.value.length,
      ...params
    });
    achatsMagasin.value = achatsMagasin.value.concat(res.data);
    if (res.data.length === 0) {
      haveMoreAchats.value = false;
    }
  } catch (e) {
    if (e.response.data.code === 403) {
      haveMoreAchats.value = false;
      achatIsLoading.value = false;
      return;
    }
    if (e.response && e.response.status) {
      errorTickets.value = e.response.status;
    } else {
      errorTickets.value = "error";
    }
  }
  achatIsLoading.value = false;
};
</script>

<template>
  <div>
    <component
      :is="component"
      :has-carte-carrefour="hasCarteCarrefour"
      :type-achat="typeAchat"
      :achats-magasin="achatsMagasin"
      :error="errorTickets"
      :achat-is-loading="achatIsLoading"
      :liste-magasins="listeMagasinsSorted"
      :applied-filters="appliedFilters"
      :query-filters="$route.query"
      :key-periode="libellesPeriode.key"
      :filters-display="filtersDisplay"
      :show-see-more-results="showSeeMoreResults"
      :is-magasin-teasing="isMagasinTeasing"
      :is-fid-carrefour="isFidCarrefour"
      @appliquerFiltres="appliquerFiltres"
      @supprimerFiltre="supprimerFiltre"
      @supprimerToutFiltres="supprimerToutFiltres"
      @voirPlusAchats="getAchats"
    />
  </div>
</template>
